import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'

import { Pencil } from '@/components/icons'
import { Alert } from '@/components/ui/alert/alert'
import { Button } from '@/components/ui/button'
import { PageCard, PageLayout, PageTwoColumn } from '@/components/ui/page'
import { Table } from '@/components/ui/table/table'
import { Text } from '@/components/ui/text'

import { toIdr } from '@/libs/utils/format'

import { productDetail } from '@/features/product/api'

export const Route = createFileRoute('/_app/product/$id/')({
	loader: async ({ context, params }) => {
		const { qc } = context
		const { id } = params

		await qc.ensureQueryData(productDetail.qry({ id }))
	},
	component: Component,
})

function Component() {
	const { product } = useData()

	return (
		<PageLayout.Root>
			<PageLayout.Header
				breadcrumbs={[
					['/product', 'Produk'],
					['/product/$id', product.name],
				]}
			/>
			<PageTwoColumn>
				<PageTwoColumn.Main>
					<GeneralSection />
					<VariantSection />
				</PageTwoColumn.Main>
				<PageTwoColumn.Sidebar>
					<RecipeSection />
				</PageTwoColumn.Sidebar>
			</PageTwoColumn>
		</PageLayout.Root>
	)
}

function useData() {
	const { id } = Route.useParams()
	const { product } = useSuspenseQuery(productDetail.qry({ id })).data
	return { product }
}

function GeneralSection() {
	const { product } = useData()

	return (
		<PageCard.Root>
			<PageCard.Header
				title="Informasi Produk"
				actions={
					<PageCard.HeaderActionRoot>
						<PageCard.HeaderAction asChild>
							<Link from={Route.fullPath} to="/product/$id/update">
								<Pencil /> Edit
							</Link>
						</PageCard.HeaderAction>
					</PageCard.HeaderActionRoot>
				}
			/>
			<PageCard.ContentRow title="ID" value={product.id} />
			<PageCard.ContentRow title="Nama Produk" value={product.name} />
			<PageCard.ContentRow
				title="Deskripsi"
				value={product.desc}
				classNameTitle="self-start"
			/>
			<PageCard.ContentRow title="Harga" value={toIdr(product.price)} />
		</PageCard.Root>
	)
}

function VariantSection() {
	const { product } = useData()
	const { variants, hasVariant } = product

	return (
		<PageCard.Root>
			<PageCard.Header title="Varian" desc={`${variants.length} varian`} />
			{!hasVariant && (
				<PageCard.ContentSection>
					<Text className="col-span-full">
						Product ini tidak memiliki varian
					</Text>
				</PageCard.ContentSection>
			)}
			{hasVariant && (
				<Table>
					<Table.Header>
						<Table.Row>
							<Table.HeaderCell>Varian</Table.HeaderCell>
							<Table.HeaderCell>Harga</Table.HeaderCell>
							<Table.HeaderCell>Default</Table.HeaderCell>
						</Table.Row>
					</Table.Header>
					<Table.Body>
						{variants.map((variant) => (
							<Table.Row key={variant.id}>
								<Table.Cell>{variant.name}</Table.Cell>
								<Table.Cell>{toIdr(variant.price)}</Table.Cell>
								<Table.Cell>{variant.isDefault ? 'Yes' : 'No'}</Table.Cell>
							</Table.Row>
						))}
					</Table.Body>
				</Table>
			)}
		</PageCard.Root>
	)
}

function RecipeSection() {
	const { product } = useData()

	return (
		<PageCard.Root>
			<PageCard.Header title="Resep" />
			<PageCard.ContentSection>
				{!product.recipeId && (
					<Alert className="col-span-full">
						<p>
							Produk ini belum memiliki resep. Tambahkan resep agar dapat
							memanfaatkan fitur stok.
						</p>
					</Alert>
				)}

				<Button asChild>
					<Link from={Route.fullPath} to="/product/$id/recipe">
						{product.recipeId ? 'Ubah' : 'Tambah'} Resep
					</Link>
				</Button>
			</PageCard.ContentSection>
		</PageCard.Root>
	)
}
